import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  COUNTRIES,
  COURSES,
  QUALIFICATIONS,
  INTAKE_YEARS,
  TRACKS,
  PROGRAM_LEVELS,
} from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  contact_number: "",
  email: "",
  interest_track: "",
  program_level: "",
  education_qualification: "",
  intake_year: "",
  preferred_country: "",
  preferred_course: "",
};

const selectCls =
  "w-full bg-white/80 border-border/70 h-11 rounded-lg data-[placeholder]:text-muted-foreground";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-secondary/80">{label}</Label>
      {children}
    </div>
  );
}

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isAbroad = form.interest_track === "Study Abroad";

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "name", "contact_number", "email", "interest_track",
      "program_level", "education_qualification", "intake_year", "preferred_course",
    ];
    if (isAbroad) required.push("preferred_country");
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please complete all fields", {
          description: `Missing: ${k.replace(/_/g, " ")}`,
        });
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!isAbroad) payload.preferred_country = "India";
      await axios.post(`${API}/leads`, payload);
      setDone(true);
      toast.success("Thank you! Our Bhubaneswar team will reach out shortly.");
      setForm(EMPTY);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.", {
        description: err?.response?.data?.detail?.[0]?.msg || "",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="lead-form-card"
      className="rounded-2xl border border-white/40 bg-white/80 backdrop-blur-xl shadow-2xl shadow-secondary/30 p-6 sm:p-7"
    >
      <div className="mb-5">
        <h3 className="font-display text-2xl font-semibold text-secondary">
          Start Your Journey
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Study abroad or study in India — free counselling, book today.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name">
            <Input data-testid="lead-name" placeholder="Your name" value={form.name}
              onChange={(e) => set("name", e.target.value)} className="bg-white/80 border-border/70 h-11" />
          </Field>
          <Field label="Contact Number">
            <Input data-testid="lead-contact" placeholder="+91 ..." value={form.contact_number}
              onChange={(e) => set("contact_number", e.target.value)} className="bg-white/80 border-border/70 h-11" />
          </Field>
        </div>

        <Field label="Email Address">
          <Input type="email" data-testid="lead-email" placeholder="you@email.com" value={form.email}
            onChange={(e) => set("email", e.target.value)} className="bg-white/80 border-border/70 h-11" />
        </Field>

        <Field label="I'm interested in">
          <Select value={form.interest_track} onValueChange={(v) => set("interest_track", v)}>
            <SelectTrigger data-testid="lead-track" className={selectCls}>
              <SelectValue placeholder="Choose your track" />
            </SelectTrigger>
            <SelectContent>
              {TRACKS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Program Level">
            <Select value={form.program_level} onValueChange={(v) => set("program_level", v)}>
              <SelectTrigger data-testid="lead-level" className={selectCls}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {PROGRAM_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Intake Year">
            <Select value={form.intake_year} onValueChange={(v) => set("intake_year", v)}>
              <SelectTrigger data-testid="lead-intake" className={selectCls}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {INTAKE_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Current Qualification">
            <Select value={form.education_qualification} onValueChange={(v) => set("education_qualification", v)}>
              <SelectTrigger data-testid="lead-qualification" className={selectCls}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {QUALIFICATIONS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Field of Study">
            <Select value={form.preferred_course} onValueChange={(v) => set("preferred_course", v)}>
              <SelectTrigger data-testid="lead-course" className={selectCls}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {COURSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {isAbroad && (
          <Field label="Preferred Country">
            <Select value={form.preferred_country} onValueChange={(v) => set("preferred_country", v)}>
              <SelectTrigger data-testid="lead-country" className={selectCls}>
                <SelectValue placeholder="Select destination" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.filter((c) => c !== "India").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}

        <Button
          type="submit"
          disabled={loading || done}
          data-testid="lead-submit"
          className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium group"
        >
          {loading ? (
            <><Loader2 className="animate-spin mr-2" size={18} /> Submitting...</>
          ) : done ? (
            <><CheckCircle2 className="mr-2" size={18} /> Received!</>
          ) : (
            <>Book Free Counselling <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} /></>
          )}
        </Button>
      </form>
    </div>
  );
}
